"use client"

import { useGetEmailTemplate } from "@/hooks/email-templates"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"

import { T } from "@/i18n"

interface TemplateViewProps {
  templateId: string
  onBack: () => void
}

export function TemplateView({ templateId, onBack }: TemplateViewProps) {
  const { data: template, isLoading, isError } = useGetEmailTemplate(templateId)

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    )
  }

  if (isError || !template) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">
          <T tKey="operations.templates.view.error" />
        </p>
      </div>
    )
  }

  return (
    <div>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        <T tKey="common.back" />
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>{template.name}</CardTitle>
          <CardDescription>
            <Badge>{template.type}</Badge>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.templates.fields.subject" />
            </h3>
            <p>{template.subject}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.templates.fields.description" />
            </h3>
            <p>
              {/* Use optional chaining for description property */}
              {template?.subject || (
                <span className="italic">
                  <T tKey="common.noDescription" />
                </span>
              )}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.templates.fields.body" />
            </h3>
            <div dangerouslySetInnerHTML={{ __html: template.htmlContent }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.templates.fields.createdAt" />
            </h3>
            <p>{new Date(template.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.templates.fields.updatedAt" />
            </h3>
            <p>{new Date(template.updatedAt).toLocaleString()}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
